import i18n from '@/i18n'
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import {
  contactMatchesScope,
  clampAttributionIdForSave,
} from '@/lib/chatContactScope';

export interface ContactInteraction {
  id: string;
  title: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContactOpportunity {
  id: string;
  pipeline_id: string;
  pipeline_stage_id: string;
  pipeline_name?: string;
  stage_name?: string;
  stage_order?: number;
  title: string;
  product?: string;
  price?: number;
  tags?: string[];
  status: 'open' | 'won' | 'lost';
  sale_date?: string | null;
  won_by_user_id?: string | null;
  won_by_user_name?: string | null;
  lost_by_user_id?: string | null;
  notes?: string | null;
  closed_at?: string | null;
  loss_reason_id?: string | null;
  loss_reason_name?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContactDetails {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  wa_id: string | null;
  custom_fields: Record<string, any> | null;
  tags: string[];
  campaigns: string[];
  assistant_id: string | null;
  assistant_is_active: boolean;
  assistant_name?: string;
  attribution_id: string | null;
  opportunities: ContactOpportunity[];
  interactions: ContactInteraction[];
  profile_picture: string | null;
  created_at: string;
  updated_at: string;
  last_interaction_at: string | null;
}

export function useContactDetails(contactId: string | undefined) {
  const { user } = useAuth();
  const [contact, setContact] = useState<ContactDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const accountId = user?.account?.id;

  const fetchContactDetails = async () => {
    if (!contactId || !accountId || !supabase) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch contact with assistant info
      const { data: contactData, error: contactError } = await supabase
        .from('account_contacts')
        .select(`
          *,
          assistant:account_assistants!account_contacts_account_assistant_id_fkey(
            id,
            name
          )
        `)
        .eq('id', contactId)
        .eq('account_id', accountId)
        .maybeSingle();

      if (contactError) {
        console.error('Error fetching contact:', contactError);
        throw contactError;
      }

      if (!contactData) {
        setError(i18n.t('crm:errors.contactNotFound'));
        setLoading(false);
        return;
      }

      if (!contactMatchesScope(user, { attribution_id: contactData.attribution_id ?? null })) {
        setError(i18n.t('crm:errors.noPermissionViewContact'));
        setContact(null);
        setLoading(false);
        return;
      }

      // Fetch opportunities with pipeline info, won by user info, and loss reason
      const { data: opportunities, error: oppError } = await supabase
        .from('account_opportunities')
        .select(`
          *,
          pipeline:account_pipelines!account_opportunities_pipeline_id_fkey(
            id,
            name
          ),
          stage:account_pipeline_stages!account_opportunities_stage_id_fkey(
            id,
            name,
            order_index
          ),
          won_by_user:account_users!account_opportunities_won_by_user_id_fkey(
            id,
            name,
            email
          ),
          loss_reason:account_loss_reasons(
            id,
            name
          )
        `)
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false });

      if (oppError) throw oppError;

      // Fetch interactions
      const { data: interactions, error: intError } = await supabase
        .from('account_contact_interactions')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: false });

      if (intError) throw intError;

      // Fetch latest message timestamp for last_interaction_at
      const { data: lastMessage } = await supabase
        .from('account_messages')
        .select('sent_at')
        .eq('account_contact_id', contactId)
        .order('sent_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      // Transform data with fallbacks for incomplete data
      const transformedContact: ContactDetails = {
        id: contactData.id,
        name: contactData.name || contactData.display_name || contactData.push_name_last || 'Nome não informado',
        email: contactData.email || null,
        phone: contactData.phone || null,
        wa_id: contactData.wa_id || contactData.jid || null,
        custom_fields: contactData.custom_fields || null,
        tags: Array.isArray(contactData.tags) ? contactData.tags : [],
        campaigns: Array.isArray(contactData.campaigns) ? contactData.campaigns : [],
        assistant_id: contactData.account_assistant_id || null,
        assistant_is_active: contactData.assistant_is_active || false,
        assistant_name: contactData.assistant?.name || undefined,
        attribution_id: contactData.attribution_id || null,
        profile_picture: contactData.profile_picture || null,
        opportunities: Array.isArray(opportunities) ? opportunities.map(opp => ({
          id: opp.id,
          pipeline_id: opp.pipeline_id,
          pipeline_stage_id: opp.stage_id,
          pipeline_name: opp.pipeline?.name || 'Pipeline',
          stage_name: opp.stage?.name || i18n.t('common:shared.stage'),
          stage_order: opp.stage?.order_index || 0,
          title: opp.title || '',
          product: opp.product || '',
          price: opp.price || 0,
          tags: opp.tags || [],
          status: opp.status || 'open',
          sale_date: opp.sale_date || null,
          won_by_user_id: opp.won_by_user_id || null,
          won_by_user_name: opp.won_by_user ? (opp.won_by_user.name || opp.won_by_user.email) : null,
          lost_by_user_id: opp.lost_by_user_id || null,
          notes: opp.notes || null,
          closed_at: opp.closed_at || null,
          loss_reason_id: opp.loss_reason_id || null,
          loss_reason_name: opp.loss_reason?.name || null,
          created_at: opp.created_at,
          updated_at: opp.updated_at,
        })) : [],
        interactions: Array.isArray(interactions) ? interactions : [],
        created_at: contactData.created_at || new Date().toISOString(),
        updated_at: contactData.updated_at || new Date().toISOString(),
        last_interaction_at: lastMessage?.sent_at || null,
      };

      setContact(transformedContact);
    } catch (err: any) {
      console.error('Error fetching contact details:', err);
      setError(err.message || 'Erro ao carregar dados do contato');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContactDetails();
  }, [contactId, accountId]);

  const updateContact = async (updates: Partial<ContactDetails>) => {
    if (!contactId || !accountId || !supabase) return false;

    try {
      const safeAttribution =
        updates.attribution_id !== undefined
          ? clampAttributionIdForSave(updates.attribution_id, user ?? null)
          : undefined;

      const { error } = await supabase
        .from('account_contacts')
        .update({
          name: updates.name,
          display_name: updates.name, // Atualiza display_name junto com name
          email: updates.email,
          phone: updates.phone,
          custom_fields: updates.custom_fields,
          tags: updates.tags,
          campaigns: updates.campaigns,
          assistant_is_active: updates.assistant_is_active,
          account_assistant_id: updates.assistant_id,
          ...(safeAttribution !== undefined ? { attribution_id: safeAttribution } : {}),
        })
        .eq('id', contactId)
        .eq('account_id', accountId);

      if (error) throw error;

      await fetchContactDetails();
      return true;
    } catch (err: any) {
      console.error('Error updating contact:', err);
      setError(err.message);
      return false;
    }
  };

  const addInteraction = async (title: string, description: string) => {
    if (!contactId || !accountId || !supabase) return false;

    try {
      const { error } = await supabase
        .from('account_contact_interactions')
        .insert({
          account_id: accountId,
          contact_id: contactId,
          title,
          description,
        });

      if (error) throw error;

      await fetchContactDetails();
      return true;
    } catch (err: any) {
      console.error('Error adding interaction:', err);
      setError(err.message);
      return false;
    }
  };

  const updateInteraction = async (interactionId: string, title: string, description: string) => {
    if (!accountId || !supabase) return false;

    try {
      const { error } = await supabase
        .from('account_contact_interactions')
        .update({ title, description })
        .eq('id', interactionId)
        .eq('account_id', accountId);

      if (error) throw error;

      await fetchContactDetails();
      return true;
    } catch (err: any) {
      console.error('Error updating interaction:', err);
      setError(err.message);
      return false;
    }
  };

  const deleteInteraction = async (interactionId: string) => {
    if (!accountId || !supabase) return false;

    try {
      const { error } = await supabase
        .from('account_contact_interactions')
        .delete()
        .eq('id', interactionId)
        .eq('account_id', accountId);

      if (error) throw error;

      await fetchContactDetails();
      return true;
    } catch (err: any) {
      console.error('Error deleting interaction:', err);
      setError(err.message);
      return false;
    }
  };

  return {
    contact,
    loading,
    error,
    updateContact,
    addInteraction,
    updateInteraction,
    deleteInteraction,
    refresh: fetchContactDetails,
  };
}
